// PoolLeadsPage.js — Pietila Group platform
// Residential inground pool leads found by the Bid Scout agent (table: public.pool_leads).
// RLS: pool_leads is readable/writable by profiles with role 'owner' or 'office_staff'.

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { supabase } from '../../lib/supabase';

const TYPES = {
  inbound: 'Inbound inquiry',
  zba_variance: 'ZBA variance',
  new_home_permit: 'New home permit',
  pool_permit: 'Pool permit',
  fence_permit: 'Fence permit',
  builder: 'Builder',
  landscape_architect: 'Landscape architect',
  other: 'Other',
};
const TYPE_ORDER = Object.keys(TYPES);
const STATUSES = ['new', 'contacted', 'qualified', 'quoted', 'won', 'lost', 'not_interested', 'do_not_contact'];
const STATUS_COLORS = {
  new: '#4a6a8c', contacted: '#6a7ab4', qualified: '#4a8ca8', quoted: '#b48c3c',
  won: '#4a8c6a', lost: 'rgba(255,255,255,0.3)', not_interested: 'rgba(255,255,255,0.3)', do_not_contact: '#e05c5c',
};

const fmtDate = (d) => (d ? new Date(d.length === 10 ? d + 'T12:00:00' : d).toLocaleDateString() : '—');
const isUnanswered = (l) => l.lead_type === 'inbound' && l.status === 'new' && (l.notes || '').includes('UNANSWERED');
const label = (s) => s.replace(/_/g, ' ');

const EMPTY_LEAD = {
  lead_type: 'inbound', lead_source: 'manual', name: '', company: '', email: '', phone: '',
  address: '', city: '', township: '', details: '', fit_score: 3, notes: '',
};

export default function PoolLeadsPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('open');
  const [townFilter, setTownFilter] = useState('all');
  const [minFit, setMinFit] = useState(1);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('priority');
  const [selected, setSelected] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error: err } = await supabase
      .from('pool_leads')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(1000);
    if (err) setError(err.message);
    else { setLeads(data || []); setError(''); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const towns = useMemo(
    () => [...new Set(leads.map((l) => l.township || l.city).filter(Boolean))].sort(),
    [leads]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = leads.filter((l) => {
      if (typeFilter !== 'all' && l.lead_type !== typeFilter) return false;
      if (statusFilter === 'open' && ['won', 'lost', 'not_interested', 'do_not_contact'].includes(l.status)) return false;
      if (statusFilter !== 'open' && statusFilter !== 'all' && l.status !== statusFilter) return false;
      if (townFilter !== 'all' && (l.township || l.city) !== townFilter) return false;
      if ((l.fit_score || 0) < minFit) return false;
      if (q) {
        const hay = [l.name, l.company, l.address, l.city, l.township, l.details, l.notes, l.email, l.phone]
          .join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const byDate = (a, b, k) => new Date(b[k] || 0) - new Date(a[k] || 0);
    rows.sort((a, b) => {
      if (sortBy === 'priority') {
        const u = Number(isUnanswered(b)) - Number(isUnanswered(a));
        if (u) return u;
        const f = (b.fit_score || 0) - (a.fit_score || 0);
        if (f) return f;
        return byDate(a, b, 'created_at');
      }
      if (sortBy === 'fit') return (b.fit_score || 0) - (a.fit_score || 0);
      if (sortBy === 'event') return byDate(a, b, 'event_date');
      return byDate(a, b, 'created_at');
    });
    return rows;
  }, [leads, typeFilter, statusFilter, townFilter, minFit, search, sortBy]);

  const counts = useMemo(() => {
    const c = { total: 0, unanswered: 0, newThisWeek: 0 };
    const weekAgo = Date.now() - 7 * 864e5;
    TYPE_ORDER.forEach((t) => (c[t] = 0));
    leads.forEach((l) => {
      if (['won', 'lost', 'not_interested', 'do_not_contact'].includes(l.status)) return;
      c.total += 1;
      c[l.lead_type] = (c[l.lead_type] || 0) + 1;
      if (isUnanswered(l)) c.unanswered += 1;
      if (new Date(l.created_at).getTime() > weekAgo) c.newThisWeek += 1;
    });
    return c;
  }, [leads]);

  const updateLead = async (id, patch) => {
    const body = { ...patch, updated_at: new Date().toISOString() };
    const { data, error: err } = await supabase.from('pool_leads').update(body).eq('id', id).select().single();
    if (err) { alert('Save failed: ' + err.message); return; }
    setLeads((prev) => prev.map((l) => (l.id === id ? data : l)));
    setSelected((s) => (s && s.id === id ? data : s));
  };

  const markContacted = (l) =>
    updateLead(l.id, {
      status: l.status === 'new' ? 'contacted' : l.status,
      last_contacted_at: new Date().toISOString(),
      notes: (l.notes || '').replace('UNANSWERED', 'ANSWERED'),
    });

  const deleteLead = async (l) => {
    const who = l.company || l.name || 'this lead';
    if (!window.confirm(`Delete ${who}? This removes the lead for good.`)) return;
    const { error: err } = await supabase.from('pool_leads').delete().eq('id', l.id);
    if (err) { alert('Delete failed: ' + err.message); return; }
    setLeads((prev) => prev.filter((x) => x.id !== l.id));
    setSelected((s) => (s && s.id === l.id ? null : s));
  };

  const addLead = async (lead) => {
    const row = { ...lead, fit_score: Number(lead.fit_score) || null, notes: lead.notes ? `[Manual] ${lead.notes}` : null };
    const { data, error: err } = await supabase.from('pool_leads').insert(row).select().single();
    if (err) { alert('Add failed: ' + err.message); return; }
    setLeads((prev) => [data, ...prev]);
    setAdding(false);
  };

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <h1 style={S.h1}>POOL LEADS</h1>
          <div style={S.sub}>Residential inground prospects · found by Bid Scout (Mon/Thu) · outreach is manual</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={S.btnGhost} onClick={load}>Refresh</button>
          <button style={S.btn} onClick={() => setAdding(true)}>+ Add lead</button>
        </div>
      </div>

      <div style={S.tiles}>
        <Tile label="Open leads" value={counts.total} />
        <Tile label="Unanswered inquiries" value={counts.unanswered} alert={counts.unanswered > 0} onClick={() => { setTypeFilter('inbound'); setStatusFilter('new'); }} />
        <Tile label="New this week" value={counts.newThisWeek} />
        <Tile label="ZBA variances" value={counts.zba_variance} onClick={() => setTypeFilter('zba_variance')} />
        <Tile label="New home permits" value={counts.new_home_permit} onClick={() => setTypeFilter('new_home_permit')} />
        <Tile label="Builders / LAs" value={(counts.builder || 0) + (counts.landscape_architect || 0)} onClick={() => setTypeFilter('builder')} />
      </div>

      <div style={S.filters}>
        <input style={{ ...S.input, minWidth: 200, flex: 1 }} placeholder="Search name, address, notes…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select style={S.input} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="all">All types</option>
          {TYPE_ORDER.map((t) => <option key={t} value={t}>{TYPES[t]}</option>)}
        </select>
        <select style={S.input} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="open">Open (active)</option>
          <option value="all">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}
        </select>
        <select style={S.input} value={townFilter} onChange={(e) => setTownFilter(e.target.value)}>
          <option value="all">All towns</option>
          {towns.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select style={S.input} value={minFit} onChange={(e) => setMinFit(Number(e.target.value))}>
          {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>Fit ≥ {n}</option>)}
        </select>
        <select style={S.input} value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="priority">Sort: priority</option>
          <option value="newest">Sort: newest found</option>
          <option value="event">Sort: event date</option>
          <option value="fit">Sort: fit score</option>
        </select>
      </div>

      {error && <div style={S.error}>Couldn't load leads: {error}</div>}
      {loading ? (
        <div style={S.empty}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={S.empty}>No leads match these filters. Bid Scout adds new ones every Monday and Thursday.</div>
      ) : (
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>
                {['Fit', 'Type', 'Name / Company', 'Town', 'What it is', 'Found', 'Status', ''].map((h) => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr key={l.id} style={{ ...S.tr, backgroundColor: isUnanswered(l) ? 'rgba(224,92,92,0.07)' : undefined }} onClick={() => setSelected(l)}>
                  <td style={S.td}><FitDots n={l.fit_score} /></td>
                  <td style={S.td}>
                    <span style={S.typeChip}>{TYPES[l.lead_type] || l.lead_type}</span>
                    {isUnanswered(l) && <span style={S.unanswered}>UNANSWERED</span>}
                  </td>
                  <td style={S.td}>
                    <div style={S.strong}>{l.company || l.name || '—'}</div>
                    {l.company && l.name && <div style={S.muted}>{l.name}</div>}
                  </td>
                  <td style={S.td}>{l.township || l.city || '—'}</td>
                  <td style={{ ...S.td, maxWidth: 360 }}><div style={S.clamp}>{l.details || '—'}</div></td>
                  <td style={S.td}>{fmtDate(l.event_date || l.created_at)}</td>
                  <td style={S.td}><StatusPill s={l.status} /></td>
                  <td style={S.td} onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {l.status !== 'do_not_contact' && (
                        <button style={S.btnSmall} onClick={() => markContacted(l)}>Contacted</button>
                      )}
                      <button style={S.btnDanger} title="Delete lead" onClick={() => deleteLead(l)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && <LeadDrawer lead={selected} onClose={() => setSelected(null)} onSave={updateLead} onContacted={markContacted} onDelete={deleteLead} />}
      {adding && <AddLeadModal onClose={() => setAdding(false)} onAdd={addLead} />}
    </div>
  );
}

function LeadDrawer({ lead, onClose, onSave, onContacted, onDelete }) {
  const [status, setStatus] = useState(lead.status);
  const [notes, setNotes] = useState(lead.notes || '');
  const [fit, setFit] = useState(lead.fit_score || 3);
  useEffect(() => { setStatus(lead.status); setNotes(lead.notes || ''); setFit(lead.fit_score || 3); }, [lead]);
  const dirty = status !== lead.status || notes !== (lead.notes || '') || Number(fit) !== (lead.fit_score || 3);
  const mapsUrl = lead.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([lead.address, lead.city, 'MI'].filter(Boolean).join(', '))}` : null;

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.drawer} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
          <div>
            <div style={S.typeChip}>{TYPES[lead.lead_type]}</div>
            <h2 style={S.drawerTitle}>{lead.company || lead.name || 'Lead'}</h2>
            {lead.company && lead.name && <div style={S.muted}>{lead.name}{lead.contact_role ? ` · ${lead.contact_role}` : ''}</div>}
          </div>
          <button style={S.btnGhost} onClick={onClose}>✕</button>
        </div>

        <dl style={S.dl}>
          <Row k="Address" v={lead.address ? <>{lead.address}{mapsUrl && <> · <a style={S.link} href={mapsUrl} target="_blank" rel="noreferrer">map</a></>}</> : null} />
          <Row k="Town" v={[lead.city, lead.township, lead.county].filter(Boolean).join(' · ')} />
          <Row k="Lot" v={lead.lot_acres ? `${lead.lot_acres} acres` : null} />
          <Row k="Phone" v={lead.phone && <a style={S.link} href={`tel:${lead.phone}`}>{lead.phone}</a>} />
          <Row k="Email" v={lead.email && <a style={S.link} href={`mailto:${lead.email}`}>{lead.email}</a>} />
          <Row k="Website" v={lead.website && <a style={S.link} href={lead.website} target="_blank" rel="noreferrer">{lead.website}</a>} />
          <Row k="Source" v={lead.lead_source} />
          <Row k="Event date" v={lead.event_date && fmtDate(lead.event_date)} />
          <Row k="Found" v={fmtDate(lead.created_at)} />
          <Row k="Last contacted" v={lead.last_contacted_at && fmtDate(lead.last_contacted_at)} />
          <Row k="Source link" v={lead.source_url && <a style={S.link} href={lead.source_url} target="_blank" rel="noreferrer">open record ↗</a>} />
        </dl>

        {lead.details && (<><div style={S.sectionLabel}>Details</div><p style={S.p}>{lead.details}</p></>)}

        <div style={S.sectionLabel}>Status & fit</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <select style={S.input} value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}
          </select>
          <select style={S.input} value={fit} onChange={(e) => setFit(e.target.value)}>
            {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>Fit {n}</option>)}
          </select>
        </div>

        <div style={S.sectionLabel}>Notes</div>
        <textarea style={{ ...S.input, width: '100%', minHeight: 140, fontFamily: 'inherit' }} value={notes} onChange={(e) => setNotes(e.target.value)} />

        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button style={S.btn} disabled={!dirty} onClick={() => onSave(lead.id, { status, notes, fit_score: Number(fit) })}>Save</button>
          {lead.status !== 'do_not_contact' && <button style={S.btnGhost} onClick={() => onContacted(lead)}>Mark contacted today</button>}
          <button style={{ ...S.btnDanger, marginLeft: 'auto', padding: '8px 14px', fontSize: 11 }} onClick={() => onDelete(lead)}>Delete lead</button>
        </div>
      </div>
    </div>
  );
}

function AddLeadModal({ onClose, onAdd }) {
  const [f, setF] = useState(EMPTY_LEAD);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div style={S.overlayCenter} onClick={onClose}>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ ...S.drawerTitle, marginTop: 0 }}>ADD POOL LEAD</h2>
        <div style={S.formGrid}>
          <label style={S.lbl}>Type<select style={S.input} value={f.lead_type} onChange={set('lead_type')}>{TYPE_ORDER.map((t) => <option key={t} value={t}>{TYPES[t]}</option>)}</select></label>
          <label style={S.lbl}>Source<input style={S.input} value={f.lead_source} onChange={set('lead_source')} placeholder="referral, show, Facebook…" /></label>
          <label style={S.lbl}>Name<input style={S.input} value={f.name} onChange={set('name')} /></label>
          <label style={S.lbl}>Company<input style={S.input} value={f.company} onChange={set('company')} /></label>
          <label style={S.lbl}>Phone<input style={S.input} value={f.phone} onChange={set('phone')} /></label>
          <label style={S.lbl}>Email<input style={S.input} value={f.email} onChange={set('email')} /></label>
          <label style={S.lbl}>Address<input style={S.input} value={f.address} onChange={set('address')} /></label>
          <label style={S.lbl}>City<input style={S.input} value={f.city} onChange={set('city')} /></label>
          <label style={S.lbl}>Township<input style={S.input} value={f.township} onChange={set('township')} /></label>
          <label style={S.lbl}>Fit (1–5)<select style={S.input} value={f.fit_score} onChange={set('fit_score')}>{[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}</select></label>
        </div>
        <label style={{ ...S.lbl, marginTop: 8 }}>What they want<textarea style={{ ...S.input, minHeight: 70, fontFamily: 'inherit' }} value={f.details} onChange={set('details')} /></label>
        <label style={{ ...S.lbl, marginTop: 8 }}>Notes<textarea style={{ ...S.input, minHeight: 60, fontFamily: 'inherit' }} value={f.notes} onChange={set('notes')} /></label>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button style={S.btn} disabled={!f.name && !f.company} onClick={() => onAdd(f)}>Add lead</button>
          <button style={S.btnGhost} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

const Row = ({ k, v }) => (v ? (<><dt style={S.dt}>{k}</dt><dd style={S.dd}>{v}</dd></>) : null);
const Tile = ({ label: l, value, alert, onClick }) => (
  <div style={{ ...S.tile, cursor: onClick ? 'pointer' : 'default', borderColor: alert ? 'rgba(224,92,92,0.35)' : 'rgba(255,255,255,0.06)', backgroundColor: alert ? 'rgba(224,92,92,0.08)' : 'rgba(255,255,255,0.02)' }} onClick={onClick}>
    <div style={{ fontFamily: COND, fontSize: 26, fontWeight: 700, color: alert ? '#e05c5c' : '#ffffff' }}>{value || 0}</div>
    <div style={{ fontFamily: COND, fontSize: 10, letterSpacing: 1.5, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase' }}>{l}</div>
  </div>
);
const FitDots = ({ n }) => (
  <span title={`Fit ${n || '?'}/5`} style={{ whiteSpace: 'nowrap' }}>
    {[1, 2, 3, 4, 5].map((i) => (
      <span key={i} style={{ display: 'inline-block', width: 7, height: 7, borderRadius: 4, marginRight: 2, backgroundColor: i <= (n || 0) ? '#b48c3c' : 'rgba(255,255,255,0.12)' }} />
    ))}
  </span>
);
const StatusPill = ({ s }) => (
  <span style={{ display: 'inline-block', fontFamily: COND, fontSize: 10, fontWeight: 700, letterSpacing: 1.5, padding: '3px 8px', borderRadius: 3, backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid ' + (STATUS_COLORS[s] || 'rgba(255,255,255,0.2)'), color: STATUS_COLORS[s] || 'rgba(255,255,255,0.4)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{label(s)}</span>
);

var COND = "'Barlow Condensed', sans-serif";

const S = {
  page: { fontFamily: "'Barlow', sans-serif", maxWidth: '100%', color: '#ffffff' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16, marginBottom: 24 },
  h1: { margin: 0, fontFamily: COND, fontSize: 28, fontWeight: 700, letterSpacing: 3, color: '#ffffff' },
  sub: { color: 'rgba(255,255,255,0.4)', fontSize: 15, marginTop: 4 },
  tiles: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, marginBottom: 20 },
  tile: { backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 4, padding: '14px 16px' },
  filters: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 },
  input: { padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 3, color: '#ffffff', fontSize: 14, fontFamily: "'Barlow', sans-serif", outline: 'none', boxSizing: 'border-box' },
  btn: { padding: '8px 16px', backgroundColor: '#4a8c6a', border: 'none', borderRadius: 3, color: '#ffffff', fontFamily: COND, fontSize: 12, fontWeight: 700, letterSpacing: 2, cursor: 'pointer' },
  btnGhost: { padding: '8px 14px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 3, color: 'rgba(255,255,255,0.45)', fontFamily: COND, fontSize: 11, fontWeight: 600, letterSpacing: 1.5, cursor: 'pointer' },
  btnDanger: { padding: '4px 8px', backgroundColor: 'rgba(224,92,92,0.1)', border: '1px solid rgba(224,92,92,0.3)', borderRadius: 3, color: '#e05c5c', fontFamily: COND, fontSize: 9, fontWeight: 700, letterSpacing: 1, cursor: 'pointer', whiteSpace: 'nowrap' },
  btnSmall: { padding: '4px 8px', backgroundColor: 'rgba(74,140,106,0.15)', border: '1px solid rgba(74,140,106,0.3)', borderRadius: 3, color: '#4a8c6a', fontFamily: COND, fontSize: 9, fontWeight: 700, letterSpacing: 1, cursor: 'pointer', whiteSpace: 'nowrap' },
  tableWrap: { overflowX: 'auto', backgroundColor: 'rgba(255,255,255,0.01)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 4 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '12px 14px', borderBottom: '1px solid rgba(255,255,255,0.08)', fontFamily: COND, fontSize: 10, fontWeight: 600, letterSpacing: 2, color: 'rgba(255,255,255,0.3)', whiteSpace: 'nowrap' },
  tr: { cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.04)' },
  td: { padding: '12px 14px', verticalAlign: 'top', color: 'rgba(255,255,255,0.7)' },
  typeChip: { display: 'inline-block', fontFamily: COND, fontSize: 10, fontWeight: 600, letterSpacing: 1.5, padding: '3px 8px', borderRadius: 3, backgroundColor: 'rgba(180,140,60,0.12)', border: '1px solid rgba(180,140,60,0.3)', color: '#b48c3c', whiteSpace: 'nowrap' },
  unanswered: { display: 'inline-block', marginLeft: 6, fontFamily: COND, fontSize: 9, fontWeight: 700, letterSpacing: 1, padding: '3px 6px', borderRadius: 2, backgroundColor: 'rgba(224,92,92,0.15)', border: '1px solid rgba(224,92,92,0.35)', color: '#e05c5c', whiteSpace: 'nowrap' },
  muted: { color: 'rgba(255,255,255,0.4)', fontSize: 12 },
  strong: { fontSize: 13, fontWeight: 600, color: '#ffffff' },
  link: { color: '#4a6a8c', textDecoration: 'none' },
  clamp: { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
  empty: { padding: 60, textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: 15, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 4 },
  error: { padding: 12, backgroundColor: 'rgba(224,92,92,0.1)', border: '1px solid rgba(224,92,92,0.25)', color: '#e05c5c', borderRadius: 3, marginBottom: 12, fontSize: 13 },
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'flex-end', zIndex: 1000 },
  overlayCenter: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 },
  drawer: { backgroundColor: '#0f1420', borderLeft: '1px solid rgba(255,255,255,0.1)', width: '100%', maxWidth: 480, height: '100%', overflowY: 'auto', padding: 28, boxSizing: 'border-box', fontFamily: "'Barlow', sans-serif", color: '#ffffff' },
  modal: { backgroundColor: '#0f1420', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: 32, width: '100%', maxWidth: 560, maxHeight: '85vh', overflow: 'auto', boxSizing: 'border-box', fontFamily: "'Barlow', sans-serif", color: '#ffffff' },
  drawerTitle: { margin: '10px 0 2px', fontFamily: COND, fontSize: 22, fontWeight: 700, letterSpacing: 2, color: '#ffffff' },
  dl: { display: 'grid', gridTemplateColumns: '110px 1fr', gap: '8px 12px', margin: '20px 0', fontSize: 13 },
  dt: { fontFamily: COND, fontSize: 10, letterSpacing: 2, color: 'rgba(255,255,255,0.35)', paddingTop: 2 },
  dd: { margin: 0, wordBreak: 'break-word', color: 'rgba(255,255,255,0.75)' },
  sectionLabel: { fontFamily: COND, fontSize: 12, fontWeight: 600, letterSpacing: 2, color: '#b48c3c', margin: '20px 0 8px' },
  p: { margin: 0, fontSize: 13, lineHeight: 1.6, color: 'rgba(255,255,255,0.7)', whiteSpace: 'pre-wrap' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  lbl: { display: 'flex', flexDirection: 'column', gap: 4, fontFamily: COND, fontSize: 10, letterSpacing: 2, color: 'rgba(255,255,255,0.35)' },
};
